// src/services/voteServices.js

import axios from "axios";

import { getVoteAuthDebug } from "../pages/Admin/VotePage/utils/voteAuthDebug";

const baseUrl = `${process.env.REACT_APP_API_URL}/api`;

const getAuthConfig = () => {
  const token = localStorage.getItem("token");

  return {
    headers: token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {},
  };
};

const logAndThrow = (message, error) => {
  console.error(message, error);
  throw error;
};

/// ===========================
/// VOTES
/// ===========================

export const getAllVotes = async () => {
  try {
    const response = await axios.get(`${baseUrl}/vote/all`, getAuthConfig());

    return response.data;
  } catch (error) {
    return logAndThrow("Erreur lors du chargement des votes :", error);
  }
};

export const getVoteById = async (id) => {
  try {
    const response = await axios.get(`${baseUrl}/vote/${id}`, getAuthConfig());

    return response.data;
  } catch (error) {
    return logAndThrow("Erreur lors du chargement du vote :", error);
  }
};

// GET - Groupes d'acteurs disponibles pour l'audience admin
export const getVoteAudienceActorGroups = async () => {
  try {
    const response = await axios.get(
      `${baseUrl}/vote/audiences/actor-groups`,
      getAuthConfig(),
    );

    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error("Erreur lors du chargement des groupes d'acteurs :", error);

    throw error;
  }
};

export const getVoteResults = async (voteId) => {
  try {
    const response = await axios.get(`${baseUrl}/vote/${voteId}/results`);

    return response.data;
  } catch (error) {
    return logAndThrow("Erreur lors de la récupération des résultats :", error);
  }
};

export const getVoteResultsDetailed = async (voteId) => {
  try {
    const response = await axios.get(
      `${baseUrl}/vote/${voteId}/results-detailed`,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    return logAndThrow(
      "Erreur lors de la récupération des résultats détaillés :",
      error,
    );
  }
};

export const createVote = async (voteData) => {
  const token = localStorage.getItem("token");

  if (!token) {
    throw new Error("Aucun token d’authentification disponible.");
  }

  try {
    const response = await axios.post(`${baseUrl}/vote/create`, voteData, {
      headers: {
        Authorization: `Bearer ${token}`,

        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la création du vote :", {
      status: error.response?.status,

      data: error.response?.data,

      message: error.message,
    });

    throw error;
  }
};

export const updateVote = async (id, updatedData) => {
  try {
    const response = await axios.put(
      `${baseUrl}/vote/update/${id}`,
      updatedData,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    return logAndThrow("Erreur lors de la mise à jour du vote :", error);
  }
};

export const deleteVote = async (id) => {
  try {
    const response = await axios.delete(
      `${baseUrl}/vote/delete/${id}`,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    return logAndThrow("Erreur lors de la suppression du vote :", error);
  }
};

/// ===========================
/// QUESTIONS
/// ===========================

export const addQuestionToVote = async (voteId, questionData) => {
  try {
    const response = await axios.post(
      `${baseUrl}/vote/${voteId}/questions`,
      questionData,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    return logAndThrow("Erreur lors de l’ajout de la question :", error);
  }
};

export const updateQuestion = async (questionId, updatedData) => {
  try {
    const response = await axios.put(
      `${baseUrl}/question/update/${questionId}`,
      updatedData,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    return logAndThrow("Erreur lors de la mise à jour de la question :", error);
  }
};

export const deleteQuestion = async (questionId) => {
  try {
    const response = await axios.delete(
      `${baseUrl}/question/delete/${questionId}`,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    return logAndThrow("Erreur lors de la suppression de la question :", error);
  }
};

/// ===========================
/// CHOIX
/// ===========================

export const addChoiceToQuestion = async (questionId, choiceData) => {
  try {
    const response = await axios.post(
      `${baseUrl}/question/${questionId}/choices`,
      choiceData,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    return logAndThrow("Erreur lors de l’ajout du choix :", error);
  }
};

export const updateChoice = async (choiceId, updatedData) => {
  try {
    const response = await axios.put(
      `${baseUrl}/choice/update/${choiceId}`,
      updatedData,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    return logAndThrow("Erreur lors de la mise à jour du choix :", error);
  }
};

export const deleteChoice = async (choiceId) => {
  try {
    const response = await axios.delete(
      `${baseUrl}/choice/delete/${choiceId}`,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    return logAndThrow("Erreur lors de la suppression du choix :", error);
  }
};

/// ===========================
/// BULLETINS
/// ===========================

export const submitVote = async (votePayload) => {
  try {
    const response = await axios.post(
      `${baseUrl}/vote/submit`,
      votePayload,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    return logAndThrow("Erreur lors de la soumission du vote :", error);
  }
};

// GET - Toutes les réponses d'un utilisateur
export const getUserBallots = async (userId) => {
  try {
    const response = await axios.get(
      `${baseUrl}/vote/ballots/${userId}`,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement des réponses :", error);

    throw error;
  }
};

/// ===========================
/// TOKENS D’INVITATION
/// ===========================

export const generateTokens = async (voteId, users) => {
  try {
    const response = await axios.post(
      `${baseUrl}/vote/${voteId}/generate-tokens`,
      { users },
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    return logAndThrow("Erreur lors de la génération des tokens :", error);
  }
};

export const checkToken = async (token) => {
  try {
    const response = await axios.get(`${baseUrl}/vote/check-token/${token}`);

    return response.data;
  } catch (error) {
    return logAndThrow("Erreur lors de la vérification du token :", error);
  }
};

export const getVoteTokens = async (voteId) => {
  try {
    const response = await axios.get(
      `${baseUrl}/vote/${voteId}/tokens`,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    return logAndThrow("Erreur lors du chargement des tokens :", error);
  }
};

export const deleteToken = async (tokenId) => {
  try {
    const response = await axios.delete(
      `${baseUrl}/vote/token/delete/${tokenId}`,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    return logAndThrow("Erreur lors de la suppression du token :", error);
  }
};
